"use client";

import { useRef, useState, useCallback, useContext, useMemo } from "react";
import type { CreateDropConfig } from "@/entities/IDrop";
import CreateDropStormParts from "./CreateDropStormParts";
import { AnimatePresence, motion } from "framer-motion";
import CreateDropContent from "./CreateDropContent";
import CreateCurationDropContent from "./CreateCurationDropContent";
import { useMutation } from "@tanstack/react-query";
import type { ApiWave } from "@/generated/models/ApiWave";
import { ReactQueryWrapperContext } from "../react-query-wrapper/ReactQueryWrapper";
import { commonApiPost } from "@/services/api/common-api";
import type { ApiCreateDropRequest } from "@/generated/models/ApiCreateDropRequest";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { AuthContext } from "../auth/Auth";
import { useProgressiveDebounce } from "@/hooks/useProgressiveDebounce";
import { useKeyPressEvent } from "react-use";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { CurationComposerVariant } from "./PrivilegedDropCreator";
import { DropMode } from "./PrivilegedDropCreator";
import type { DropPrivileges } from "@/hooks/useDropPriviledges";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import { ProcessIncomingDropType } from "@/contexts/wave/hooks/useWaveRealtimeUpdater";
import { useUnreadDividerOptional } from "@/contexts/wave/UnreadDividerContext";
import { useWave } from "@/hooks/useWave";
interface CreateDropProps {
  readonly activeDrop: ActiveDropState | null;
  readonly onCancelReplyQuote: () => void;
  readonly onDropAddedToQueue: () => void;
  readonly onAllDropsAdded?: (() => void) | undefined;
  readonly wave: ApiWave;
  readonly dropId: string | null;
  readonly fixedDropMode: DropMode;
  readonly privileges: DropPrivileges;
  readonly curationComposerVariant?: CurationComposerVariant | undefined;
}
export interface DropMutationBody {
  readonly drop: ApiCreateDropRequest;
  readonly dropId: string | null;
}
const ANIMATION_DURATION = 0.3;
export default function CreateDrop({
  activeDrop,
  onCancelReplyQuote,
  onDropAddedToQueue,
  onAllDropsAdded,
  wave,
  dropId,
  fixedDropMode,
  privileges,
  curationComposerVariant = "default"
}: CreateDropProps) {
  const {
    setToast
  } = useContext(AuthContext);
  const {
    waitAndInvalidateDrops
  } = useContext(ReactQueryWrapperContext);
  const unreadDividerContext = useUnreadDividerOptional();
  useKeyPressEvent("Escape", () => onCancelReplyQuote());
  const [isStormMode, setIsStormMode] = useState(false);
  const [drop, setDrop] = useState<CreateDropConfig | null>(null);
  const [dropModeOverride, setDropModeOverride] = useState<{
    scopeKey: string;
    value: boolean;
  } | null>(null);
  const [curationPrefillSeed, setCurationPrefillSeed] = useState<{
    scopeKey: string;
    url: string;
  } | null>(null);
  const {
    processDropRemoved,
    processIncomingDrop
  } = useMyStream();
  const {
    isCurationWave
  } = useWave(wave);
  const getDefaultIsDropMode = () => {
    if (fixedDropMode === DropMode.CHAT) {
      return false;
    }
    if (fixedDropMode === DropMode.PARTICIPATION) {
      return true;
    }
    if (wave.chat.authenticated_user_eligible) return false;
    if (wave.participation.authenticated_user_eligible) return true;
    if (activeDrop) return false;
    return false;
  };
  const activeDropScope = activeDrop === null ? "none" : `${activeDrop.action}:${activeDrop.drop.id}:${activeDrop.partId}`;
  const modeScopeKey = `${wave.id}:${fixedDropMode}:${wave.chat.authenticated_user_eligible}:${wave.participation.authenticated_user_eligible}:${activeDropScope}`;
  const modeScopeEpochRef = useRef(0);
  const lastModeScopeKeyRef = useRef(modeScopeKey);
  if (lastModeScopeKeyRef.current !== modeScopeKey) {
    lastModeScopeKeyRef.current = modeScopeKey;
    modeScopeEpochRef.current += 1;
  }
  const modeScopeToken = `${modeScopeKey}:${modeScopeEpochRef.current}`;
  const defaultIsDropMode = getDefaultIsDropMode();
  const isDropMode = dropModeOverride?.scopeKey === modeScopeToken ? dropModeOverride.value : defaultIsDropMode;
  const initialCurationUrl = curationPrefillSeed?.scopeKey === modeScopeToken ? curationPrefillSeed.url : null;
  const isCurationDropMode = isCurationWave && isDropMode;
  const canSwitchDropMode = useCallback((newIsDropMode: boolean) => {
    if (fixedDropMode !== DropMode.BOTH) {
      return false;
    }
    if (newIsDropMode && !wave.participation.authenticated_user_eligible) {
      setToast({
        message: "You are not eligible to drop in this wave",
        type: "error"
      });
      return false;
    }
    if (!newIsDropMode && !wave.chat.authenticated_user_eligible) {
      setToast({
        message: "You are not eligible to chat in this wave",
        type: "error"
      });
      return false;
    }
    return true;
  }, [fixedDropMode, setToast, wave]);
  const onDropModeChange = useCallback((newIsDropMode_0: boolean) => {
    if (!canSwitchDropMode(newIsDropMode_0)) {
      return;
    }
    setCurationPrefillSeed(null);
    setDropModeOverride({
      scopeKey: modeScopeToken,
      value: newIsDropMode_0
    });
  }, [canSwitchDropMode, modeScopeToken]);
  const onSwitchToDropModeWithUrl = useCallback((url: string) => {
    if (!canSwitchDropMode(true)) {
      return;
    }
    setCurationPrefillSeed({
      scopeKey: modeScopeToken,
      url
    });
    setDropModeOverride({
      scopeKey: modeScopeToken,
      value: true
    });
  }, [canSwitchDropMode, modeScopeToken]);
  const onRemovePart = useCallback((partIndex: number) => {
    setDrop(prevDrop => {
      if (!prevDrop) return null;
      const newParts = prevDrop.parts.filter((_, i) => i !== partIndex);
      return {
        ...prevDrop,
        parts: newParts,
        referenced_nfts: prevDrop.referenced_nfts,
        mentioned_users: prevDrop.mentioned_users,
        mentioned_waves: prevDrop.mentioned_waves ?? [],
        metadata: prevDrop.metadata
      };
    });
  }, []);
  const addDropMutation = useMutation({
    mutationFn: async (body: DropMutationBody) => {
      return commonApiPost<ApiCreateDropRequest, ApiDrop>({
        endpoint: `drops`,
        body: body.drop
      });
    },
    onSuccess: (serverDrop, body_0) => {
      if (body_0.dropId) {
        processDropRemoved(body_0.drop.wave_id, body_0.dropId);
      }
      processIncomingDrop(serverDrop, ProcessIncomingDropType.DROP_INSERT);
      if (isCurationWave && curationComposerVariant === "leaderboard") {
        setToast({
          message: "Drop submitted successfully",
          type: "success"
        });
      }
    },
    onError: (error, body_1) => {
      setTimeout(() => {
        if (body_1.dropId) {
          processDropRemoved(body_1.drop.wave_id, body_1.dropId);
        }
      }, 0);
      setToast({
        message: error instanceof Error ? error.message : String(error),
        type: "error"
      });
    },
    retry: failureCount => {
      if (failureCount >= 3) {
        return false;
      }
      return true;
    },
    retryDelay: failureCount_0 => {
      return failureCount_0 * 1000;
    }
  });

  // Use refs to avoid stale closures - fixes the stream unmounting issue
  const queueRef = useRef<DropMutationBody[]>([]);
  const isProcessingRef = useRef(false);
  const [hasQueueChanged, setHasQueueChanged] = useState(false);
  useProgressiveDebounce(() => {
    if (queueRef.current.length === 0 && !isProcessingRef.current && hasQueueChanged) {
      waitAndInvalidateDrops();
      onAllDropsAdded?.();
    }
  }, [hasQueueChanged], {
    minDelay: 1000,
    maxDelay: 4000,
    increaseFactor: 1.5,
    decreaseFactor: 1.2
  });
  const processNextDrop = useCallback(async () => {
    if (isProcessingRef.current || queueRef.current.length === 0) {
      return;
    }
    isProcessingRef.current = true;
    const dropRequest = queueRef.current.shift();
    if (dropRequest) {
      try {
        await addDropMutation.mutateAsync(dropRequest);
      } catch (error_0) {
        console.error("Error processing drop:", error_0);
      }
    }
    isProcessingRef.current = false;

    // Process next item if queue has more
    if (queueRef.current.length > 0) {
      processNextDrop();
    }
  }, [addDropMutation]);
  const submitDrop = useCallback((dropRequest_0: DropMutationBody) => {
    // Add to queue
    queueRef.current.push(dropRequest_0);
    setHasQueueChanged(true);

    // Process immediately - avoids state update timing issues
    processNextDrop();

    // Clear unread divider when user sends a message
    if (unreadDividerContext) {
      unreadDividerContext.setUnreadDividerSerialNo(null);
    }

    // Trigger UI updates
    onDropAddedToQueue();

    // Explicitly blur any focused input to close keyboard
    (document.activeElement as HTMLElement)?.blur();
  }, [onDropAddedToQueue, processNextDrop, unreadDividerContext]);
  const createDropContentProps = useMemo(() => ({
    activeDrop,
    onCancelReplyQuote,
    drop,
    isStormMode,
    isDropMode,
    dropId,
    setDrop,
    setIsStormMode,
    onDropModeChange,
    onSwitchToDropModeWithUrl,
    submitDrop,
    privileges
  }), [activeDrop, onCancelReplyQuote, drop, isStormMode, isDropMode, dropId, setDrop, setIsStormMode, onDropModeChange, onSwitchToDropModeWithUrl, submitDrop, privileges]);
  return <>
      <AnimatePresence>
        {isStormMode && !isCurationDropMode && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: "auto"
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: ANIMATION_DURATION
      }}>
            <CreateDropStormParts parts={drop?.parts ?? []} mentionedUsers={drop?.mentioned_users ?? []} mentionedWaves={drop?.mentioned_waves ?? []} referencedNfts={drop?.referenced_nfts ?? []} onRemovePart={onRemovePart} />
          </motion.div>}
      </AnimatePresence>

      {isCurationDropMode ? <CreateCurationDropContent activeDrop={activeDrop} onCancelReplyQuote={onCancelReplyQuote} wave={wave} dropId={dropId} isDropMode={isDropMode} initialUrl={initialCurationUrl} submitDrop={submitDrop} curationComposerVariant={curationComposerVariant} /> : <CreateDropContent {...createDropContentProps} wave={wave} />}
    </>;
}